import Client from './Client'

export interface IRenameRequest {
  newPath: string
}

export interface IFileInfo {
  isDir: boolean
  path: string
}

export interface IFolderInfo {
  path: string
  filesInfo: IFileInfo[]
}

export interface IBrokerScript {
  code: string
}

export interface IProcess {
  replica: string
  pid: number
  path: string
}

export interface IDependencies {
  list: IDependency[]
}

export interface IDependency {
  name: string
  version: string
}

export interface IReplica {
  id: string
  appName: string
  version: string
  goVersion: string
  buildDate: string
  commit: string
}

export interface IProcessList {
  list: IProcessInfo[]
}

export interface IProcessInfo {
  replica: string
  pid: number
  path: string
  createdAt: number
  memory: number
  cpu: number
}

export interface ICronRules {
  rules: ICronItem[]
}

export interface ICronItem {
  path: string
  timer: ITimer
}

export interface ITimer {
  type: string
  active: boolean
  start: number
  next: number
  repeat: ITimerRepeat
}

export interface ITimerRepeat {
  // Only one (Depends on of the timer type)
  custom: ITimerRepeatCustom
  Daily: ITimerRepeatDaily
  Monthly: ITimerRepeatMonthly
}

export interface ITimerRepeatCustom {
  minutes: number
}

export interface ITimerRepeatDaily {
  days: number[]
  minutes: number
}

export interface ITimerRepeatMonthly {
  months: number[]
  days: number[]
  lastDate: boolean
  minutes: number
}

export interface ILogsParts {
  part: number
  countParts: number
  data: string
}

export interface ILogsLines {
  lines: string
}

export interface IAuthSettings {
  database: {
    connection: string
    userTable: {
      name: string
      columnNameLogin: string
      columnNamePassword: string
      columnNameBlocked: string
    }
  }
  password: {
    salt: string
    algorithm: string
    cost: number
    rules: string
    minLength: number
  }
  session: {
    autoProlongate: boolean
    lifetime: string
  }
}

// export interface API {
//   rules: APIItem[]
// }
//
// export interface APIItem {
//   path: string
//   url: string
//   method: string
//   needAuth: boolean
// }

const API_PREFIX = '/manager/api/v2'

/**
 * Класс для работы с апи v1.0.1
 */
export default class Core {
  constructor(public readonly client: Client) {}

  /*
    Folder API
   */

  // Прочесть содержимое папки
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  folderRead(path: string): Promise<IFolderInfo> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/folders/${path}`)
      .execute()
  }

  // Создать папку
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // folder_exists
  folderCreate(path: string): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/folders`)
      .postJson({ path })
      .execute()
  }

  // переименовать папку
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // folder_not_found
  // folder_exists
  folderRename(path: string, payload: IRenameRequest): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/folders/${path}`)
      .postJson(payload)
      .execute()
  }

  // Удалить папку
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // folder_not_found
  // folder_not_empty
  folderDelete(path: string): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/folders/${path}`)
      .delete()
      .execute()
  }

  /*
    Script API
   */

  // Прочесть содержимое скрипта
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  scriptRead(path: string): Promise<IBrokerScript> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/scripts/${path}`)
      .execute()
  }

  // Создать скрипт
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // script_exists
  scriptCreate(path: string, payload: IBrokerScript): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/scripts/${path}`)
      .postJson(payload)
      .execute()
  }

  // Переименовать скрипт
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // folder_not_found
  // folder_exists
  scriptRename(path: string, payload: IRenameRequest): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/scripts/${path}`)
      .postJson(payload)
      .execute()
  }

  // Обновить скрипт
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  scriptUpdate(path: string, payload: IBrokerScript): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/scripts/${path}`)
      .patchJson(payload)
      .execute()
  }

  // Удалить скрипт
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  scriptDelete(path: string): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/scripts/${path}`)
      .delete()
      .execute()
  }

  /*
    Script Run API
   */

  // Запустить скрипт
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  asyncScriptRun(path: string): Promise<IProcess> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/ascripts/run/${path}`)
      .postJson({})
      .execute()
  }

  // Остановить скрипт
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  asyncScriptStop(replica: string, pid: number): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/broker/ascripts/run/${replica}/${pid}`
      )
      .delete()
      .execute()
  }

  /*
  Script Dependencies API
 */

  // Обновить зависимости
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  dependenciesUpdate(payload: IDependencies): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/dependencies`)
      .postJson(payload)
      .execute()
  }

  // Очистить все зависимости
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  dependenciesFlush(): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/dependencies`)
      .delete()
      .execute()
  }

  /*
  Web Server API
 */

  // Запустить веб сервер
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  webServerRun(replica: string): Promise<IReplica> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/webserver/run`)
      .postJson({
        id: replica,
      })
      .execute()
  }

  // Остановить веб сервер
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  webServerStop(replica: string): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/webserver/stop`)
      .postJson({
        id: replica,
      })
      .execute()
  }

  /*
  Processes API
 */

  // Получить список процессов
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  processes(): Promise<IProcessList> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/processes`)
      .execute()
  }

  /*
  Cron API
 */

  // Получить список задач для крона
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  cronRead(): Promise<ICronRules> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/crons`)
      .execute()
  }

  // Изменить задачи для крона
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  cronSet(payload: ICronRules): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/broker/crons`)
      .postJson(payload)
      .execute()
  }

  /*
    Logs API
  */

  // Получить часть логов
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  getLogs(part = 1): Promise<ILogsParts> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/logs/parts/${part}`)
      .execute()
  }

  // Загрузить дамп логов
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  getLogsDump(): Promise<Blob> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/logs/dump`)
      .setResponseType('blob')
      .execute()
  }

  // Загрузить последние count* строк логов
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  getLogsTail(count: number): Promise<ILogsLines> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/logs/tail/${count}`)
      .execute()
  }

  // Загрузить последние count* строк логов
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  clearLogs(): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/logs`)
      .delete()
      .execute()
  }

  // Authorization service
  getAuthSettings(): Promise<IAuthSettings> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/auth/settings`)
      .execute()
  }

  setAuthSettings(payload: IAuthSettings): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/auth/settings`)
      .postJson(payload)
      .execute()
  }

  // PUSH service
  updateSettingsFCM(payload: Blob): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/push/settings/fcm`)
      .post(payload)
      .execute()
  }

  updateSettingsAPN(payload: Blob): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/push/settings/apn`)
      .post(payload)
      .execute()
  }
}
